/**
 * ReportShipDataProvider - Listens for report ship data requests from popups
 * 
 * This component runs in the main window and handles requests for report
 * ship data by fetching via useGetExtraJobData and responding.
 */
import { useEffect, useState, useCallback } from 'react';
import { useGetExtraJobData } from '@ais-ui/websockets';
import { useTimeZoneContext } from '@ais-ui/utils';
import { 
  usePopupMessages, 
  MainWindowActions, 
  MessageType,
  PopupMessage,
  DataType,
} from './PopupEvents';

interface ActiveRequest {
  popupId: string;
  jobId: string;
  bounds?: {
    minLat: number;
    maxLat: number;
    minLon: number;
    maxLon: number;
  };
}

export const ReportShipDataProvider = (): null => {
  const [activeRequest, setActiveRequest] = useState<ActiveRequest | null>(null);
  const { timeZone } = useTimeZoneContext();

  // Default bounds that include everything (no filtering)
  const defaultBounds = { minLat: -90, maxLat: 90, minLon: -180, maxLon: 180 };
  const bounds = activeRequest?.bounds || defaultBounds;

  // Fetch ship data when we have an active request
  const { data: shipData, isLoading, isError } = useGetExtraJobData(
    activeRequest?.jobId,
    timeZone,
    bounds.minLat,
    bounds.maxLat,
    bounds.minLon,
    bounds.maxLon
  );

  // When data arrives, send it to the popup
  useEffect(() => {
    if (activeRequest && !isLoading) {
      const { popupId } = activeRequest;
      
      if (isError) {
        MainWindowActions.sendDataUpdate(DataType.SHIP_DATA, { 
          data: [], 
          error: 'Failed to fetch ship data' 
        }, popupId);
      } else if (shipData) {
        MainWindowActions.sendDataUpdate(DataType.SHIP_DATA, {
          data: shipData,
        }, popupId);
      }
      
      // Clear active request after responding
      setActiveRequest(null);
    }
  }, [shipData, isLoading, isError, activeRequest]);

  // Listen for ship data requests
  usePopupMessages(useCallback((msg: PopupMessage) => {
    // Listen for requests for ship data (can come as part of report init or separate)
    if (msg.type === MessageType.POPUP_INIT) {
      const payload = msg.payload as { 
        popupType: string;
        popupId: string;
        params: { 
          jobId?: string;
          requestShipData?: boolean;
          bounds?: {
            minLat: number;
            maxLat: number;
            minLon: number;
            maxLon: number;
          };
        };
      };
      
      // Only handle if this is a report popup requesting ship data
      if (payload.popupType === 'report' && payload.params.jobId && payload.params.requestShipData) {
        setActiveRequest({
          popupId: payload.popupId,
          jobId: payload.params.jobId,
          bounds: payload.params.bounds,
        });
      }
    }

    // Also listen for explicit data requests (for refreshing)
    if (msg.type === MessageType.VIEW_TARGET) {
      const payload = msg.payload as { 
        action: string;
        jobId?: string;
        popupId?: string;
        bounds?: {
          minLat: number;
          maxLat: number;
          minLon: number;
          maxLon: number;
        };
      };
      
      if (payload.action === 'request-ship-data' && payload.jobId && payload.popupId) {
        setActiveRequest({
          popupId: payload.popupId,
          jobId: payload.jobId,
          bounds: payload.bounds,
        });
      }
    }
  }, []), []);

  return null;
};
